import React, { FC, useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, IconButton, MenuItem, Select } from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import { addWarehouses, getClients, getWarehouses, addWarehouseCompanyMapping, addWarehouseUserMapping,addClientVendorWarehouseMapping } from './action.ts';
import AddClientsModal from '../Clients/add-clients-modal.tsx';
import { validateEmail } from '../../Utils/EmailValidation/email-validation.tsx';
import { validateDriverName } from '../../Utils/NameValidation/name-validation.tsx';
import { validateRemarks } from '../../Utils/RemarksValidation/remarks-validation.tsx';
import { validatePhoneNumber } from '../../Utils/PhoneNumberValidation/phone-number-validation.tsx';
import FindWarehouseModal from './find-warehouse-modal.tsx';
import MapModal from './map-modal.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

interface AddWarehousesModalProps {
    open: boolean;
    onClose: () => void;
}

const AddWarehousesModal: FC<AddWarehousesModalProps> = ({ open, onClose }) => {

    const [warehouseName, setWarehouseName] = useState('');
    const [contactName, setContactName] = useState<string>('');
    const [warehouseNameError, setWarehouseNameError] = useState<string>('');
    const [contactNameError, setContactNameError] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [remarks, setRemarks] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [remarksError, setRemarksError] = useState<string>('');
    const [mobileNumber, setMobileNumber] = useState<string>('');
    const [mobileNumberError, setMobileNumberError] = useState<string>('');
    const [clientOptions, setClientOptions] = useState<{ id: string, label: string }[]>([]);
    const [selectedClient, setSelectedClient] = useState<{ id: string, name: string } | null>(null);
    const [clientNameError, setClientNameError] = useState<string>('');
    const [clientName, setClientName] = useState<string>('');
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [latitudeError, setLatitudeError] = useState<string>('');
    const [longitudeError, setLongitudeError] = useState<string>('');
    const [mapModalOpen, setMapModalOpen] = useState(false);
    const [isExistingWarehouse, setIsExistingWarehouse] = useState(false);
    const [existingWarehouseId, setExistingWarehouseId] = useState<string | null>(null);
    const [addClientsModalOpen, setAddClientsModalOpen] = useState(false);
    const [findWarehouseModalOpen, setFindWarehouseModalOpen] = useState(false);
    const [searchWarehouseName, setSearchWarehouseName] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searchError, setSearchError] = useState('');
    const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");
    const userRole = userDetailsFromStorage?.role?.toLowerCase();
    const clientNameFromStorage = userDetailsFromStorage?.client_name;
    const clientIdFromStorage = userDetailsFromStorage?.id;
    const companyId = userDetailsFromStorage?.companies_id;

    const resetFormFields = () => {
        setWarehouseName('');
        setContactName('');
        setEmail('');
        setMobileNumber('');
        setRemarks('');
        setLatitude('');
        setLongitude('');
        setClientName('');
        setExistingWarehouseId(null);
        setIsExistingWarehouse(false);
        setWarehouseNameError('');
        setContactNameError('');
        setEmailError('');
        setMobileNumberError('');
        setRemarksError('');
        setLatitudeError('');
        setLongitudeError('');
        setClientNameError('');
    };
    

    const handleCloseAddClientsModal = () => {
        setAddClientsModalOpen(false);
        if (userRole === 'vendor_company_admin' || userRole === 'vendor_supervisor') {
            fetchClients();
        }
    };

    const handleEmailBlur = () => {
        if (email === '') {
            setEmailError('');
        } else if (validateEmail(email)) {
            setEmailError('');
        } else {
            setEmailError('Please enter a valid email address.');
        }
    };

    const handleLocationSelect = (lat: string, lng: string) => {
        setLatitude(lat);
        setLongitude(lng);
        setLatitudeError('');
        setLongitudeError('');
        setMapModalOpen(false);
    };

    const handleGoogleMapsSelect = () => {
        setMapModalOpen(true);
    };

    const handleSave = () => {
        let hasError = false;

        if (!warehouseName) {
            setWarehouseNameError('Warehouse Name is required');
            hasError = true;
        }
        if (!contactName) {
            setContactNameError('Contact Name is required');
            hasError = true;
        }
        if (!mobileNumber) {
            setMobileNumberError('Mobile number is required');
            hasError = true;
        }
        if (!latitude) {
            setLatitudeError('Latitude is required');
            hasError = true;
        }
        if (!longitude) {
            setLongitudeError('Longitude is required');
            hasError = true;
        }
        if (!email) {
            setEmailError('Email is required');
            hasError = true;
        }

        const clientName = selectedClient?.name || clientNameFromStorage;
        if (!clientName) {
            setClientNameError('Client is Required');
            hasError = true;
        }

        if (hasError) {
            toast.error("Please fix the errors");
            return;
        }

        if (existingWarehouseId) {
            addWarehouseCompanyMapping(
                companyId,
                existingWarehouseId,
                (response) => {
                    toast.success("Company mapping success");
                    addWarehouseUserMapping(
                        userDetailsFromStorage.user.id,
                        existingWarehouseId,
                        (resp2) => {
                            toast.success("User mapping success");
                            resetState();
                            onClose();
                        },
                        (err2) => {
                            toast.error(err2);
                        }
                    );
                    if(userRole === 'vendor_company_admin'){
                        addClientVendorWarehouseMapping(
                            existingWarehouseId,
                            selectedClient?.id,
                            (response)=>{ console.log(response) },
                            (errMessage)=>{ toast.error(errMessage.message) }
                        );
                    }
                },
                (errorMessage) => {
                    if (errorMessage) {
                        toast.error(errorMessage);
                    }
                }
            );
        } else {
            addWarehouses(
                warehouseName,
                contactName,
                latitude,
                longitude,
                remarks,
                mobileNumber,
                email,
                (response) => {
                    toast.success(response.message);
                    if (response.status === 201) {
                        const warehouseId = response.warehouse.id;
                        setExistingWarehouseId(warehouseId);
                        if(userRole === 'vendor_company_admin' || userRole === 'vendor_supervisor' ){
                            addClientVendorWarehouseMapping(
                                warehouseId,
                                selectedClient?.id,
                                (response)=>{ console.log(response) },
                                (errMessage)=>{ console.log(errMessage); toast.error(errMessage.message) }
                            );
                        }
                    }
                    resetState();
                    setTimeout(() => {
                        window.location.reload();
                    }, 3000);
                    onClose();
                },
                (errorMessage) => {
                    if (errorMessage.message.errors.name) {
                        toast.error(errorMessage.message.errors.name[0]);
                    }
                }
            );
        }
    };

    const resetState = () => {
        setWarehouseName('');
        setWarehouseNameError('');
        setContactName('');
        setContactNameError('');
        setEmail('');
        setEmailError('');
        setMobileNumber('');
        setMobileNumberError('');
        setRemarks('');
        setRemarksError('');
        setSelectedClient(null);
        setClientNameError('');
        setLatitude('');
        setLongitude('');
        setLatitudeError('');
        setLongitudeError('');
        setExistingWarehouseId(null);
        setIsExistingWarehouse(false);
    };

    const validateLatitude = (value: string): boolean => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= -90 && num <= 90;
    };

    const validateLongitude = (value: string): boolean => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= -180 && num <= 180;
    };

    const handleLatitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLatitude(value);
        if (value === '') {
            setLatitudeError('');
        } else if (validateLatitude(value)) {
            setLatitudeError('');
        } else {
            setLatitudeError('Invalid latitude (-90 to 90)');
        }
    };

    const handleLongitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLongitude(value);
        if (value === '') {
            setLongitudeError('');
        } else if (validateLongitude(value)) {
            setLongitudeError('');
        } else {
            setLongitudeError('Invalid longitude (-180 to 180)');
        }
    };

    const handleCancel = () => {
        resetState();
        onClose();
    };

    const handleContactNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setContactName(value);
        if (value === '') {
            setContactNameError('');
        } else if (validateDriverName(value)) {
            setContactNameError('');
        } else {
            setContactNameError('Invalid Character');
        }
    };

const handleWarehouseNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        if (userRole === 'client_company_admin') {
            setWarehouseName(value);
        } else if (selectedClient) {
            const prefix = `${selectedClient.name}_`;

            if (value.startsWith(prefix)) {
                setWarehouseName(value);
            } else if (value === prefix.slice(0, -1)) {
                setWarehouseName('');
            } else if (!value.startsWith(prefix) && value.length > 0) {
                setWarehouseName(prefix + value);
            }
        } else {
            setWarehouseName(value);
        }

        if (value === '') {
            setWarehouseNameError('');
        } else if (validateRemarks(value)) {
            setWarehouseNameError('');
        } else {
            setWarehouseNameError('Invalid Character');
        }
        
        if (isExistingWarehouse) {
            setIsExistingWarehouse(false);
            setExistingWarehouseId(null);
        }
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setEmail(value);
        setEmailError('');
    };

    const handleRemarksChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setRemarks(value);
        if (value === '') {
            setRemarksError('');
        } else if (validateRemarks(value)) {
            setRemarksError('');
        } else {
            setRemarksError('Invalid Character');
        }
    };

    const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setMobileNumber(value);
        if (value === '') {
            setMobileNumberError('');
            return;
        }
        const { errorMessage } = validatePhoneNumber(value);
        setMobileNumberError(errorMessage || '');
    };

    const handleClientChange = (event: SelectChangeEvent<string>) => {
        const { value } = event.target;
        
        if (value === 'add_new') {
            setAddClientsModalOpen(true);
            return;
        }
        
        const clientId = value;
        const client = clientOptions.find(c => c.id === clientId);
        const transformedClient = client ? { id: client.id, name: client.label } : null;
        setSelectedClient(transformedClient);
        setWarehouseName('');
        
        if (value !== '') {
            setClientNameError('');
        }
    };

    const handlePhoneNumberBlur = () => {
        if (mobileNumber === '') {
            setMobileNumberError('');
        } else if (mobileNumber.length >= 1 && mobileNumber.length < 10) {
            setMobileNumberError('Please enter a 10 digit number');
        }
    };

    const handleRemarksBlur = () => {
        if (remarks === '') {
            setRemarksError('');
        } else {
            setRemarksError(remarksError);
        }
    };

    const fetchClients = () => {
        getClients(
            (response: any) => {
                const options = response.message.clients_vendors.map((clients: any) => ({
                    id: clients.id,
                    label: clients.name,
                }));
                setClientOptions(options);
            },
            (errMessage: any) => {
                toast.error(errMessage);
            }
        );
    };

    const handleFindWarehouseClick = () => {
        setFindWarehouseModalOpen(true);
        setSearchWarehouseName('');
        setSearchResults([]);
        setSearchError('');
    };

const handleSearchWarehouse = () => {
    if (!searchWarehouseName.trim()) {
        setSearchError('Please enter a warehouse name');
        return;
    }

    getWarehouses(
        0,
        "10",
        searchWarehouseName.trim(),
        (response) => {
            const list = response?.message?.warehouses || [];
            if (list.length > 0) {
                setSearchResults(list);
                setSearchError('');
            
                if (list.length === 1) {
                    handleSelectExistingWarehouse(list[0]);
                }
            } else {
                setSearchResults([]);
                setSearchError('No warehouses found with this name');
            }
        },
        (errorMessage) => {
            setSearchResults([]);
            setSearchError('Error searching for warehouses');
        }
    );
};

    const handleSelectExistingWarehouse = (warehouse: any) => {
    setWarehouseName(warehouse.name || '');
    setContactName(warehouse.contact_name || '');
    setMobileNumber(warehouse.phone || '');
    setEmail(warehouse.email || '');
    setLatitude(warehouse.latitude || '');
    setLongitude(warehouse.longitude || '');
    setRemarks(warehouse.remarks || '');
    setClientName(warehouse.client_name);
    setExistingWarehouseId(warehouse.id);
    setIsExistingWarehouse(true);
    setFindWarehouseModalOpen(false);
    setSearchWarehouseName('');
    setSearchResults([]);
    setSearchError('');
    
    toast.success(`Found existing warehouse: ${warehouse.name}`);
};
    

    useEffect(() => {
        if (userRole === 'client_company_admin') {
            setSelectedClient({ id: clientIdFromStorage, name: clientNameFromStorage })
        } else {
            fetchClients();
        }
    }, []);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ padding: '0px' }}>
                <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}>
                    <Heading title="Add Warehouses" closeIcon="true" onClose={onClose} />
                </Box>
            </DialogTitle>
            <DialogContent>
                <div style={{ marginLeft: '-10px', marginTop: '5px' }}>
                    <p style={{ fontSize: '17px' }}>Client Name</p>
                </div>
                <div style={{ marginLeft: '200px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        {(userRole === 'vendor_company_admin' || userRole === 'vendor_supervisor')  ? (
                            <Select
                                value={selectedClient?.id || ''}
                                onChange={handleClientChange}
                                displayEmpty
                                size="small"
                                error={!!clientNameError}
                                sx={{ width: '222.4px', height: '40px' }}
                            >
                                <MenuItem value="" disabled>
                                    Choose Client
                                </MenuItem>
                                {clientOptions.map((client) => (
                                    <MenuItem key={client.id} value={client.id}>
                                        {client.label}
                                    </MenuItem>
                                ))}
                                <MenuItem value="add_new" sx={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                    <AddIcon sx={{ fontSize: 18, mr: 1 }} />
                                    Add Client
                                </MenuItem>
                            </Select>
                        ) : (
                            <TextField
                                size='small'
                                name='client_name'
                                value={selectedClient?.name || clientNameFromStorage || clientName || ''}
                                variant="outlined"
                                disabled
                                fullWidth
                                sx={{ width: '222.4px' }}
                                error={!!clientNameError}
                                helperText={clientNameError}
                            />
                        )}
                        <input type="hidden" name="client_id" value={selectedClient?.id || ''} />
                    </Box>
                </div>

                <div style={{ marginLeft: '-10px', marginTop: '5px' }}>
                    <p style={{ fontSize: '17px' }}>Warehouse Name</p>
                </div>
                <div style={{ marginLeft: '200px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                            size='small'
                            name='warehouse_name'
                            placeholder='Warehouse Name'
                            value={warehouseName}
                            onChange={handleWarehouseNameChange}
                            error={!!warehouseNameError}
                            helperText={warehouseNameError}
                            variant="outlined"
                            disabled={isExistingWarehouse}
                            sx={{ width: '222.4px' }}

                        />
                        <Button
                            variant="outlined"
                            startIcon={<SearchIcon />}
                            onClick={handleFindWarehouseClick}
                            size="small"
                            sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                        >
                            Find
                        </Button>
                    </Box>
                </div>
                <div style={{ marginLeft: '-10px', marginTop: '8px' }}>
                    <p style={{ fontSize: '17px' }}>Contact Name</p>
                </div>
                <div style={{ marginLeft: '200px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='contact_name'
                            placeholder='Contact Name'
                            value={contactName}
                            onChange={handleContactNameChange}
                            error={!!contactNameError}
                            helperText={contactNameError}
                            variant="outlined"
                            disabled={isExistingWarehouse}
                        />
                    </Box>
                </div>

                <div style={{ marginLeft: '-10px', marginTop: '8px' }}>
                    <p style={{ fontSize: '17px' }}>Mobile Number</p>
                </div>
                <div style={{ marginLeft: '200px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size="small"
                            name="mobileNumber"
                            placeholder="Mobile Number"
                            value={mobileNumber}
                            onChange={handlePhoneNumberChange}
                            onBlur={handlePhoneNumberBlur}
                            error={!!mobileNumberError}
                            helperText={mobileNumberError}
                            variant="outlined"
                            disabled={isExistingWarehouse}
                        />
                    </Box>
                </div>

                <div style={{ marginLeft: '-10px', marginTop: '8px' }}>
                    <p style={{ fontSize: '17px' }}>Latitude</p>
                </div>
                <div style={{ marginLeft: '166px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <IconButton
                            onClick={handleGoogleMapsSelect}
                            color="primary"
                            aria-label="select from map"
                            style={{ marginLeft: '10px' }}
                            disabled={isExistingWarehouse}
                        >
                            <LocationOnIcon />
                        </IconButton>
                        <TextField
                            size='small'
                            name='latitude'
                            placeholder='Latitude'
                            value={latitude}
                            onChange={handleLatitudeChange}
                            error={!!latitudeError}
                            helperText={latitudeError}
                            variant="outlined"
                            disabled={isExistingWarehouse}
                        />
                    </Box>
                </div>

                <div style={{ marginLeft: '-10px', marginTop: '8px' }}>
                    <p style={{ fontSize: '17px' }}>Longitude</p>
                </div>
                <div style={{ marginLeft: '200px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='longitude'
                            placeholder='Longitude'
                            value={longitude}
                            onChange={handleLongitudeChange}
                            error={!!longitudeError}
                            helperText={longitudeError}
                            variant="outlined"
                            disabled={isExistingWarehouse}
                        />
                    </Box>
                </div>

                <div style={{ marginLeft: '-10px', marginTop: '8px' }}>
                    <p style={{ fontSize: '17px' }}>Email</p>
                </div>
                <div style={{ marginLeft: '200px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='email'
                            placeholder='Email'
                            value={email}
                            onChange={handleEmailChange}
                            onBlur={handleEmailBlur}
                            error={!!emailError}
                            helperText={emailError}
                            variant="outlined"
                            disabled={isExistingWarehouse}
                        />
                    </Box>
                </div>

                <div style={{ marginLeft: '-10px', marginTop: '8px' }}>
                    <p style={{ fontSize: '17px' }}>Remarks</p>
                </div>
                <div style={{ marginLeft: '200px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='remarks'
                            placeholder='Remarks'
                            value={remarks}
                            onChange={handleRemarksChange}
                            onBlur={handleRemarksBlur}
                            error={!!remarksError}
                            helperText={remarksError}
                            variant="outlined"
                            disabled={isExistingWarehouse}
                        />
                    </Box>
                </div>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
                <Button
                    onClick={handleCancel}
                    className="custom-cancel-button"
                    style={{ marginRight: '10px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    className="custom-button"
                    style={{ marginLeft: '10px' }}
                >
                 Save
                </Button>
            </DialogActions>

          <FindWarehouseModal
    open={findWarehouseModalOpen}
    onClose={() => setFindWarehouseModalOpen(false)}
    searchWarehouseName={searchWarehouseName}
    setSearchWarehouseName={setSearchWarehouseName}
    searchError={searchError}
    searchResults={searchResults}
    handleSearchWarehouse={handleSearchWarehouse}
    handleSelectExistingWarehouse={handleSelectExistingWarehouse}
/>

            <MapModal
                open={mapModalOpen}
                onClose={() => setMapModalOpen(false)}
                onLocationSelect={handleLocationSelect}
                initialLocation={
                    latitude && longitude
                        ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
                        : undefined
                }
            />
            <AddClientsModal 
                open={addClientsModalOpen} 
                onClose={handleCloseAddClientsModal}
            />
        </Dialog>
    );
};

export default AddWarehousesModal;